import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import api from "../../lib/api";
import { Trash } from "@phosphor-icons/react";

/**
 * Generic finance-entity editor. Renders fields from a schema.
 *
 * Schema field types:
 *   text, number, select, toggle, textarea
 */
export default function EntityDialog({
  open,
  onOpenChange,
  entity,
  schema,
  endpoint,       // e.g. "/finance/accounts"
  idField,        // e.g. "account_id"
  title,
  defaults = {},
  onSaved,
  onDeleted,
}) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isEditing = !!entity;

  useEffect(() => {
    if (open) {
      const base = { ...defaults };
      schema.forEach((f) => {
        if (base[f.key] === undefined) base[f.key] = f.default ?? "";
      });
      if (entity) {
        schema.forEach((f) => {
          if (entity[f.key] !== undefined && entity[f.key] !== null) base[f.key] = entity[f.key];
        });
      }
      setForm(base);
    }
  }, [open, entity, schema, defaults]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    // Basic validation
    const missing = schema.filter((f) => f.required && (form[f.key] === "" || form[f.key] === null || form[f.key] === undefined));
    if (missing.length > 0) {
      toast.error(`Falta: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      schema.forEach((f) => {
        let v = form[f.key];
        if (f.type === "number") v = v === "" || v === null ? null : parseFloat(v);
        if (f.type === "int") v = v === "" || v === null ? null : parseInt(v);
        if (v !== "" && v !== null && v !== undefined) payload[f.key] = v;
      });
      let saved;
      if (isEditing) {
        const { data } = await api.put(`${endpoint}/${entity[idField]}`, payload);
        saved = data;
      } else {
        const { data } = await api.post(endpoint, payload);
        saved = data;
      }
      toast.success(isEditing ? "Actualizado" : "Creado");
      onSaved?.(saved, isEditing ? "update" : "create");
      onOpenChange(false);
    } catch (err) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing) return;
    if (!window.confirm(`¿Eliminar "${entity[schema.find(s=>s.key==='name')?.key || 'name'] || 'este elemento'}"?`)) return;
    setDeleting(true);
    try {
      await api.delete(`${endpoint}/${entity[idField]}`);
      toast.success("Eliminado");
      onDeleted?.(entity);
      onOpenChange(false);
    } catch (err) {
      toast.error("No se pudo eliminar");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="entity-dialog"
        className="bg-[#0F1017] border-white/10 text-white max-w-md sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
          {schema.map((f) => {
            const span = f.full ? "col-span-2" : "col-span-2 sm:col-span-1";
            return (
              <div key={f.key} className={span}>
                <Label className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/50 mb-1.5 block">
                  {f.label}
                  {f.required && <span className="text-red-400 ml-1">*</span>}
                </Label>
                {f.type === "select" ? (
                  <select
                    data-testid={`field-${f.key}`}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono text-white focus:border-[#2563EB] outline-none"
                  >
                    {(f.options || []).map((o) => (
                      <option key={o.value} value={o.value} className="bg-[#0F1017]">
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea
                    data-testid={`field-${f.key}`}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    rows={3}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono text-white focus:border-[#2563EB] outline-none resize-none"
                  />
                ) : f.type === "toggle" ? (
                  <button
                    data-testid={`field-${f.key}`}
                    type="button"
                    onClick={() => setField(f.key, !form[f.key])}
                    className={`w-full px-3 py-2 rounded-lg border text-sm font-mono transition ${
                      form[f.key]
                        ? "bg-[#2563EB]/20 border-[#2563EB] text-white"
                        : "bg-black/40 border-white/10 text-white/50"
                    }`}
                  >
                    {form[f.key] ? "Sí" : "No"}
                  </button>
                ) : (
                  <Input
                    data-testid={`field-${f.key}`}
                    type={f.type === "number" || f.type === "int" ? "number" : "text"}
                    inputMode={f.type === "number" || f.type === "int" ? "decimal" : "text"}
                    step={f.step ?? (f.type === "number" ? "any" : undefined)}
                    placeholder={f.placeholder}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="bg-black/40 border-white/10 text-white font-mono focus:border-[#2563EB]"
                  />
                )}
                {f.hint && (
                  <div className="text-[10px] font-mono text-white/30 mt-1">{f.hint}</div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:justify-between pt-3 border-t border-white/5">
          {isEditing ? (
            <Button
              data-testid="entity-dialog-delete"
              variant="ghost"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-2"
            >
              <Trash size={14} weight="bold" /> Eliminar
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/60 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              data-testid="entity-dialog-save"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold"
            >
              {saving ? "Guardando..." : isEditing ? "Guardar" : "Crear"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
